import {
  getFirestore,
  doc,
  updateDoc,
  getDoc
} from 'firebase/firestore';
import { WebUser } from '../types/Firebase';

// Lazy initialization to avoid import-time Firebase access
function getDb() {
  return getFirestore();
}

function getWebUsersCollection() {
  return 'webUsers';
}

export class TrialService {
  /**
   * Activate trial for a user
   */
  static async activateTrial(webUserId: string): Promise<void> {
    try {
      const now = new Date();
      const trialEnd = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000); // 7 days from now

      await updateDoc(doc(getDb(), getWebUsersCollection(), webUserId), {
        'subscription.status': 'active',
        'subscription.isTrialActivated': true,
        'subscription.trialStartedAt': now,
        'subscription.trialEndsAt': trialEnd,
        'subscription.updatedAt': now
      });

      console.log(`✅ Trial activated for user ${webUserId}, expires: ${trialEnd.toISOString()}`);
    } catch (error) {
      console.error('Error activating trial:', error);
      throw new Error('Failed to activate trial');
    }
  }

  /**
   * Check if trial can be activated
   */
  static async canActivateTrial(webUserId: string): Promise<boolean> {
    try {
      const userDoc = await getDoc(doc(getDb(), getWebUsersCollection(), webUserId));

      if (!userDoc.exists()) {
        return false;
      }

      const userData = userDoc.data() as WebUser;
      const subscription = userData.subscription;

      // Trial can be activated if:
      // 1. Plan is 'trial'
      // 2. Status is 'inactive'
      // 3. Trial not already activated
      return (
        subscription?.plan === 'trial' &&
        subscription?.status === 'inactive' &&
        !subscription?.isTrialActivated
      );
    } catch (error) {
      console.error('Error checking trial eligibility:', error);
      return false;
    }
  }

  /**
   * Get trial status and remaining days
   */
  static async getTrialStatus(webUserId: string): Promise<{
    isTrialActive: boolean;
    daysRemaining: number;
    trialEndDate?: Date;
    canActivate: boolean;
  }> {
    try {
      const userDoc = await getDoc(doc(getDb(), getWebUsersCollection(), webUserId));

      if (!userDoc.exists()) {
        return { isTrialActive: false, daysRemaining: 0, canActivate: false };
      }

      const userData = userDoc.data() as WebUser;
      const subscription = userData.subscription;

      // Can activate trial?
      const canActivate = await this.canActivateTrial(webUserId);

      // Trial not activated yet
      if (!subscription?.isTrialActivated) {
        return {
          isTrialActive: false,
          daysRemaining: 7, // Full 7 days available
          canActivate
        };
      }

      // Trial activated, check if still valid
      if (subscription.trialEndsAt) {
        const now = new Date();
        const trialEnd = subscription.trialEndsAt instanceof Date ?
          subscription.trialEndsAt :
          new Date(subscription.trialEndsAt);

        if (trialEnd > now) {
          const diffTime = trialEnd.getTime() - now.getTime();
          const daysRemaining = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

          return {
            isTrialActive: true,
            daysRemaining: Math.max(0, daysRemaining),
            trialEndDate: trialEnd,
            canActivate: false
          };
        }
      }

      // Trial expired
      return {
        isTrialActive: false,
        daysRemaining: 0,
        canActivate: false
      };

    } catch (error) {
      console.error('Error getting trial status:', error);
      return { isTrialActive: false, daysRemaining: 0, canActivate: false };
    }
  }

  /**
   * Check hotel access based on subscription status
   */
  static async checkHotelAccess(hotelCode: string): Promise<{
    accessible: boolean;
    reason: 'trial_not_activated' | 'trial_active' | 'pro_active' | 'expired' | 'not_found';
    daysRemaining?: number;
    subscription?: any;
  }> {
    try {
      // Find user by hotel code (simplified - in real implementation, query webUsers collection)
      // For now, this would need to be implemented based on your hotel-user relationship

      // This is a placeholder implementation
      // You'll need to implement the logic to find user by hotel code
      throw new Error('Hotel access check not yet implemented - needs hotel-user relationship query');

    } catch (error) {
      console.error('Error checking hotel access:', error);
      return {
        accessible: false,
        reason: 'not_found'
      };
    }
  }
}

export default TrialService;